import React, { useState, useEffect, useRef } from "react";
import { Drawer } from "@blueprintjs/core";
import QuestionListPanel from "./QuestionListPanel";
import QuestionFormPanel from "./QuestionFormPanel";
import Flexbox from "flexbox-react";
import axios from "axios";
import Cookies from "js-cookie";
import ToastStore from "../../stores/ToastStore";
import ConfirmationDialog from "../../views/ConfirmationDialog";

const StepProcessQuestionManager = ({ goalCycleId, stepProcessId, isWeightAllow, jobProfileList }) => {

  const ALL_QUESTION_CATEGORY = { id: null, label: "All Category" };
  const [isQuestionSaving, setIsQuestionSaving] = useState(false);
  const questionSavePending = useRef(false);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);
  const [questionGroups, setQuestionGroups] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedJobProfileForCategory, setSelectedJobProfileForCategory] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedJobProfileFilter, setSelectedJobProfileFilter] = useState("all");
  const [isQuestionDownloading, setIsQuestionDownloading] = useState(false);
  const [isQuestionUploading, setIsQuestionUploading] = useState(false);
  const [isCategoryDownloading, setIsCategoryDownloading] = useState(false);
  const [isCategoryUploading, setIsCategoryUploading] = useState(false);
 
  useEffect(() => {
    fetchQuestions();
  }, [stepProcessId, selectedJobProfileFilter]);

  useEffect(() => {
    if (jobProfileList?.length > 0) {
      setSelectedJobProfileForCategory(jobProfileList[0]);
    } else {
      setSelectedJobProfileForCategory(null);
    }
  }, [jobProfileList]);

  useEffect(() => {
    fetchCategories(
      selectedJobProfileForCategory?.id
    );
  }, [selectedJobProfileForCategory]);

  //  API CALLS
  const fetchQuestions = () => {
    setIsQuestionsLoading(true);
    const params = {
      goal_setting_id: goalCycleId,
      review_process_step_id: stepProcessId
    };
    // ALL
    if (selectedJobProfileFilter === "all") {
      params.job_profile = "all";
    }
    // SPECIFIC JOB PROFILE
    if (selectedJobProfileFilter !== "all" && selectedJobProfileFilter !== "none") {
      params.job_profile_id = selectedJobProfileFilter;
    }
    axios.get(`${Cookies.get("apiEnv")}/review_questions`, {
      params
    })
      .then(response => {
        setQuestionGroups(response?.data || []);
      })
      .catch(error => {
        console.error("Error fetching questions", error);
      })
      .finally(() => {
        setIsQuestionsLoading(false);
      });
  };

  const fetchCategories = async (jobProfileId = null) => {
    try {
      const params = {};

      if (jobProfileId) {
        params.job_profile_id = jobProfileId;
      }

      const res = await axios.get(
        `${Cookies.get("apiEnv")}/question_categories`,
        { params }
      );

      const categories = (res.data || []).map(cat => ({
        id: cat.id,
        label: cat.name || cat.label || cat.category_name
      }))
        ;
      setCategoryOptions(categories);
    } catch (error) {
      console.error("Error fetching categories", error);
      ToastStore.showToast(
        "Failed to load categories",
        "danger"
      );
    }
  };
  //  QUESTION HANDLERS
  const handleSaveQuestion = async (formData) => {
    if (questionSavePending.current) return;
    questionSavePending.current = true;
    setIsQuestionSaving(true);
    try {
      const payload = {
        goal_setting_id: goalCycleId,
        review_process_step_id: stepProcessId,
        question: formData.question,
        question_type: formData.question_type,
        value: formData.value,
        ...(formData.job_profile_id && {
          job_profile_id: formData.job_profile_id
        }),
        ...(formData.category_id && {
          question_category_id: formData.category_id
        }),
        ...(isWeightAllow && {
          weightage: formData.weightage
        }),
      };
      // EDIT $ CREATE
      if (editingQuestion?.id) {
        await axios.put(`${Cookies.get("apiEnv")}/review_questions/${editingQuestion.id}`, payload);
        ToastStore.showToast("Question updated successfully", "success");
      } else {
        await axios.post(`${Cookies.get("apiEnv")}/review_questions`, payload);
        ToastStore.showToast("Question added successfully", "success");
      }

    } catch (error) {
      ToastStore.showToast(error?.response?.data?.error || "Failed to save question", "danger");
      return;
    } finally {
      questionSavePending.current = false;
      setIsQuestionSaving(false);
    }
    setEditingQuestion(null);
    fetchQuestions();
  };

  const handleEditQuestion = async (question, group) => {
    setEditingQuestion(question);
    setEditingGroup(group);
  };

  const handleDeleteQuestion = (id) => axios.delete(
    `${Cookies.get("apiEnv")}/review_questions/${id}?review_process_step_id=${stepProcessId}&goal_setting_id=${goalCycleId}`
  );

  // REMARK HANDLERS
  const createRemarkQuestion = async (data) => {
    return axios.post(
      `${Cookies.get("apiEnv")}/question_categories/create_remark_question`,
      {
        goal_setting_id: goalCycleId,
        review_process_step_id: stepProcessId,
        ...data
      }
    );
  };
  const updateRemarkQuestion = async (remarkId, data) => {
    return axios.put(
      `${Cookies.get("apiEnv")}/question_categories/update_remark_question/${remarkId}`,
      {
        goal_setting_id: goalCycleId,
        review_process_step_id: stepProcessId,
        ...data
      }
    );
  };

  const deleteRemarkQuestion = async (remarkId) => {
    return axios.delete(
      `${Cookies.get("apiEnv")}/question_categories/delete_remark_question/${remarkId}`,
      {
        data: { goal_setting_id: goalCycleId, review_process_step_id: stepProcessId }
      }
    );
  };

  //  QUESTION IMPORT
  const handleQuestionDownloadSample = async () => {

    try {
      setIsQuestionDownloading(true);
      const link = document.createElement("a");
      link.href = "/Review_Sample_file.xlsx";
      link.download = "Review_Sample_file.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {

      ToastStore.showToast("Failed to download sample file", "danger");

    } finally {

      setTimeout(() => {
        setIsQuestionDownloading(false);
      }, 800);
    }
  };

  const uploadQuestionFile = async ({ file, category }) => {

    try {
      setIsQuestionUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("goal_setting_id", String(goalCycleId));

      if (category?.id) {
        formData.append("category_name", category.label);
      }
      const response = await axios.post(`${Cookies.get("apiEnv")}/manager_assessments/import`, formData);
      ToastStore.showToast(
        response?.data?.message ||
        "Questions imported successfully",
        "success"
      );

      await fetchQuestions();

    } catch (error) {

      ToastStore.showToast(
        error?.response?.data?.message ||
        "Import failed",
        "danger"
      );
    } finally {
      setIsQuestionUploading(false);
    }
  };


  return (
    <>
      {isQuestionsLoading ? (
        <div>
          <Flexbox flexGrow={1} flexDirection="column" justifyContent="center" alignItems="center" style={{ marginTop: "230px" }}>
            <img
              style={{ height: "35px", width: "35px" }}
              src="https://s01.live2support.com/dashboardv2/chatassets/img/loader_new.gif"
              alt="Please wait"
            />
          </Flexbox>
        </div>
      ) : (
        <Flexbox flexDirection="row" flexGrow={1} padding="15px" style={{ width: "100%", gap: "10px" }}>

          {/* RIGHT */}
          <QuestionFormPanel
            initialData={editingQuestion}
            editingGroup={editingGroup}
            categoryOptions={categoryOptions}
            jobProfileList={jobProfileList}
            selectedJobProfileForCategory={selectedJobProfileForCategory}
            setSelectedJobProfileForCategory={setSelectedJobProfileForCategory}
            isWeightAllow={isWeightAllow}
            onSubmit={handleSaveQuestion}
            isSaving={isQuestionSaving}
          // onAddCategory={handleAddCategory}
          // onImportCategory={handleCategoryImport}
          />

          <div
            style={{
              width: "1px",
              background: "#D9D9D9",
              alignSelf: "stretch",
              minHeight: "100%",
              margin: "0 8px"
            }}
          />
          {/* LEFT */}

          <QuestionListPanel
            goalCycleId={goalCycleId}
            questionGroups={questionGroups || []}
            jobProfileList={jobProfileList}
            selectedJobProfileFilter={selectedJobProfileFilter}
            setSelectedJobProfileFilter={setSelectedJobProfileFilter}
            onEditQuestion={handleEditQuestion}
            onDeleteQuestion={handleDeleteQuestion}
            onCreateRemark={createRemarkQuestion}
            onUpdateRemark={updateRemarkQuestion}
            onDeleteRemark={deleteRemarkQuestion}
            onRefresh={fetchQuestions}
            isQuestionUploading={isQuestionUploading}
            isQuestionDownloading={isQuestionDownloading}
            onImport={{
              downloadSample: handleQuestionDownloadSample,
              upload: uploadQuestionFile
            }}
            isWeightAllow={isWeightAllow}
          />

        </Flexbox>
      )}
    </>
  );
};

export default StepProcessQuestionManager;
